import { Search } from "lucide-react";
import { type ComponentProps, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Input } from "@krea/shared/design-system/inputs/Input";

import { LenderApplicationsFilterEnum } from "@/features/common/enums";
import { useGetLenderApplicationsCountsQuery } from "@/features/lender-applications/hooks/data/use-get-lender-applications-counts.query";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { useFilterStore } from "@/store/filters-menu/hooks/useFilterStore";

import { NavGroup } from "./nav-group";

export const NavLenderApplications = () => {
  const { filters, setFilters } = useFilterStore((state) => ({
    filters: state.filters,
    setFilters: state.setFilters,
  }));
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { data: lenderApplicationsCounts } =
    useGetLenderApplicationsCountsQuery(Number(lenderCompany?.id));

  const navigate = useNavigate();
  const allApplicationsFilters = [
    LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
    LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
    LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
    LenderApplicationsFilterEnum.NO_LEASING_BIDS,
  ];

  const isFilterForAnyApp = () => {
    return (
      filters.includes(LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS) ||
      filters.includes(LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS) ||
      filters.includes(LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS) ||
      filters.includes(LenderApplicationsFilterEnum.NO_LEASING_BIDS)
    );
  };

  function onSetFilter(filters: LenderApplicationsFilterEnum[]) {
    navigate(`/lender-company/${lenderCompany?.id}`);
    setFilters(filters);
  }

  const getCountTitle = useCallback(
    (
      title: string,
      key: keyof NonNullable<typeof lenderApplicationsCounts> | "all",
    ) => {
      if (!lenderApplicationsCounts) return title;

      if (key === "all") {
        const allCount =
          lenderApplicationsCounts.no_company_loan_bids +
          lenderApplicationsCounts.no_invoice_purchase_bids +
          lenderApplicationsCounts.no_real_estate_bids +
          lenderApplicationsCounts.no_leasing_bids;

        return `${title} (${allCount})`;
      }

      return `${title} (${lenderApplicationsCounts[key]})`;
    },
    [lenderApplicationsCounts],
  );

  const sidebarItems: ComponentProps<typeof NavGroup>["items"] = [
    {
      title: getCountTitle("New applications", "all"),
      onClick: () => onSetFilter(allApplicationsFilters),
      isActive: isFilterForAnyApp(),
      items: [
        {
          title: getCountTitle(
            "New loan apps",
            LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
          ),
          onClick: () =>
            onSetFilter([LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS]),
          isActive: filters.includes(
            LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
          ),
        },
        {
          title: getCountTitle(
            "New factoring apps",
            LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
          ),
          onClick: () =>
            onSetFilter([
              LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
            ]),
          isActive: filters.includes(
            LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
          ),
        },
        {
          title: getCountTitle(
            "New real estate apps",
            LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
          ),
          onClick: () =>
            onSetFilter([LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS]),
          isActive: filters.includes(
            LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
          ),
        },
        {
          title: getCountTitle(
            "New leasing apps",
            LenderApplicationsFilterEnum.NO_LEASING_BIDS,
          ),
          onClick: () =>
            onSetFilter([LenderApplicationsFilterEnum.NO_LEASING_BIDS]),
          isActive: filters.includes(
            LenderApplicationsFilterEnum.NO_LEASING_BIDS,
          ),
        },
      ],
    },
    {
      title: getCountTitle(
        "Active bids",
        LenderApplicationsFilterEnum.HAS_OWN_BIDS,
      ),
      onClick: () => onSetFilter([LenderApplicationsFilterEnum.HAS_OWN_BIDS]),
      isActive: filters.includes(LenderApplicationsFilterEnum.HAS_OWN_BIDS),
    },
    {
      title: getCountTitle(
        "Won & Unpaid",
        LenderApplicationsFilterEnum.BID_SELECTED,
      ),
      onClick: () => onSetFilter([LenderApplicationsFilterEnum.BID_SELECTED]),
      isActive: filters.includes(LenderApplicationsFilterEnum.BID_SELECTED),
    },
    {
      title: "Archive",
      onClick: () =>
        onSetFilter([
          LenderApplicationsFilterEnum.PAID_OUT,
          LenderApplicationsFilterEnum.REJECTED,
        ]),
      isActive:
        filters.includes(LenderApplicationsFilterEnum.PAID_OUT) ||
        filters.includes(LenderApplicationsFilterEnum.REJECTED),
      items: [
        {
          title: "Paid out",
          onClick: () => onSetFilter([LenderApplicationsFilterEnum.PAID_OUT]),
          isActive: filters.includes(LenderApplicationsFilterEnum.PAID_OUT),
        },
        {
          title: "Rejected",
          onClick: () => onSetFilter([LenderApplicationsFilterEnum.REJECTED]),
          isActive: filters.includes(LenderApplicationsFilterEnum.REJECTED),
        },
      ],
    },
  ];

  const navigateToSearch = () => {
    navigate(`/lender-company/${lenderCompany?.id}/search`);
  };

  return (
    <>
      <div className="mt-2 px-4">
        <Input
          placeholder="Search applications"
          size="sm"
          readOnly
          onClick={navigateToSearch}
          leftElement={
            <Search className="text-black-100" size={16} strokeWidth={3} />
          }
        />
      </div>
      <NavGroup items={sidebarItems} />
    </>
  );
};
